import { FormEditorState, FormEditorView } from '../formeditor/formeditor-types';
import { closeForm } from '../formeditor/updaters';
import { DataLayoutPosition, FieldPosition } from '../types/template-types';
import { TRANSPARENT_COLOR, WHITE } from './contants';

export function fieldClicked(draft: FormEditorState, fieldId: string, viewType: FormEditorView) {
  // we know that when the send a form preview was loaded, all of the fields are selected by default
  // we also know that the highlighter went through and highlighted and marked all of the
  // fields as `sendAFormSelected = true`.
  // now that a field is clicked on, we can invert that selection and let the preview form component
  // handle dispatching a field selection action to the send a form preview reducer
  if (viewType === 'form') {
    return setLastFormFieldClicked(draft, fieldId, viewType);
  }

  // it's a list view
  return setLastListFieldClicked(draft, fieldId, viewType);
}

/**
 * Highlight the form fields with the given fieldIds
 *
 * @param draft The immer draft of the form editor state
 * @param selectedFieldIds The fields that are selected
 * @param color The selected field color
 */
export function hightlightSelectedFormFields(draft: FormEditorState, selectedFieldIds: string[], color: string) {
  // store the color first
  draft.sendAForm.highlightColor = color;

  // loop through the pages and field positions matching the fieldIds
  // apply the color to the field positions that match the fieldIds
  // apply a transparent color to the other field positions
  applyToAllFormFields(draft, (fieldPosition) => {
    // track the original background and focus colors of the field position before setting the new color
    // the original background color will be undefined if it's the first pass at highlighting the fields
    const isSelected = selectedFieldIds.includes(fieldPosition.fieldId);
    const fieldColor = isSelected ? color : TRANSPARENT_COLOR;

    if (!fieldPosition.originalBackgroundColor) {
      fieldPosition.originalBackgroundColor = fieldPosition.backgroundColor;
      fieldPosition.originalFocusColor = fieldPosition.focusColor;
    }

    fieldPosition.sendAFormSelected = isSelected;
    fieldPosition.backgroundColor = fieldColor;
    fieldPosition.focusColor = fieldColor;
  });

  return draft;
}

export function hightlightSelectedListFields(draft: FormEditorState, selectedFieldIds: string[], color: string) {
  draft.sendAForm.highlightColor = color;

  // loop through the data layouts looking for positions that match the fieldIds
  // apply color to the positions that match the fieldIds
  // apply a white color to the other positions
  applyToAllListFields(draft, (position) => {
    // track the original background color of the field position before setting the new color
    // the original background color will be undefined if it's the first pass at highlighting the fields
    const fieldColor = selectedFieldIds.includes(position.fieldId) ? color : WHITE;

    position.backgroundColor = fieldColor;
  });

  return draft;
}

export function closeSendAForm(draft: FormEditorState, completely?: boolean) {
  if (completely) {
    // close the form the way formeditor closes a form
    return closeForm(draft);
  }

  // revert the form and list view to their original background color4
  revertFormFieldHighlights(draft);
  revertListFieldHighlights(draft);
  // otherwise just remove the send a form state
  draft.sendAForm = undefined;

  return draft;
}

function revertFormFieldHighlights(draft: FormEditorState) {
  applyToAllFormFields(draft, (fieldPosition) => {
    if (!fieldPosition.originalBackgroundColor) {
      return;
    }

    fieldPosition.backgroundColor = fieldPosition.originalBackgroundColor;
    fieldPosition.focusColor = fieldPosition.originalFocusColor;
    delete fieldPosition.sendAFormSelected;
  });

  return draft;
}

function revertListFieldHighlights(draft: FormEditorState) {
  applyToAllListFields(draft, (position) => {
    // the original field color is white for list view
    position.backgroundColor = WHITE;
  });

  return draft;
}

function applyToAllFormFields(draft: FormEditorState, apply: (fieldPosition: FieldPosition) => void) {
  const allFieldPositions = getAllFormFieldPositions(draft);
  allFieldPositions.forEach((fieldPosition) => apply(fieldPosition));

  return draft;
}

function applyToAllListFields(draft: FormEditorState, apply: (position: DataLayoutPosition) => void) {
  const allDataLayoutPositions = getAllListDataLayoutPositions(draft);
  allDataLayoutPositions.forEach((position) => apply(position));

  return draft;
}

function findFieldInTableFieldPostion(fieldId: string, fieldPositions: FieldPosition[]) {
  const columnField = fieldPositions
    .filter((fp) => !!fp.columns)
    .flatMap((fp) => fp.columns)
    .find((col) => col.columnId === fieldId);

  return columnField;
}

function getAllFormFieldPositions(draft: FormEditorState) {
  const allFieldPositions = (draft.template?.pages ?? []).flatMap(({ fieldPositions }) => fieldPositions ?? []);

  return allFieldPositions;
}

function getAllListDataLayoutPositions(draft: FormEditorState) {
  const dataLayoutPositions = (draft.template?.dataLayouts ?? [])
    .flatMap(({ dataLayoutTabs }) => dataLayoutTabs ?? [])
    .flatMap(({ sections }) => sections ?? [])
    .flatMap(({ positions }) => positions ?? []);

  return dataLayoutPositions;
}

function setLastFormFieldClicked(draft: FormEditorState, fieldId: string, viewType: FormEditorView) {
  const fieldPositions = getAllFormFieldPositions(draft);
  // find the field position that matches the fieldId. it could be a table column that was clicked on
  const field = fieldPositions.find((fp) => fp.fieldId === fieldId);
  const column = !field ? findFieldInTableFieldPostion(fieldId, fieldPositions) : undefined;

  // make sure the field or column exists
  if (!field && !column) {
    return draft;
  }

  // the fieldId that was given should be the same as the column's field id
  draft.sendAForm.lastClickedField = { fieldId, viewType, isColumn: !!column };

  return draft;
}

function setLastListFieldClicked(draft: FormEditorState, fieldId: string, viewType: FormEditorView) {
  const dataLayoutPositions = getAllListDataLayoutPositions(draft);
  const position = dataLayoutPositions.find((p) => p.fieldId === fieldId);

  if (!position) {
    return draft;
  }

  draft.sendAForm.lastClickedField = { fieldId, viewType };

  return draft;
}
